import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface SectionHeaderProps {
  badge?: string;
  title: string;
  description: string;
  className?: string;
}

/**
 * SectionHeader - Reusable component for section headers with animations
 * 
 * @param {string} badge - Optional badge text to display above the title
 * @param {string} title - The main section title
 * @param {string} description - A descriptive text for the section
 * @param {string} className - Optional additional CSS classes
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  badge,
  title,
  description,
  className
}) => {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => {
      if (headerRef.current) {
        observer.unobserve(headerRef.current);
      }
    };
  }, []);

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      ref={headerRef} 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={variants}
      className={cn("text-center relative", className)}
    >
      {/* Decorative elements - subtle background flourish */}
      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-32 h-32 bg-gradient-to-br from-purple-100/30 to-indigo-100/30 rounded-full filter blur-2xl -z-10"></div>
      
      {badge && (
        <motion.div 
          variants={itemVariants}
          className="inline-block px-4 py-1.5 mb-6 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 shadow-sm hover:shadow-md transition-all duration-300"
        >
          <p className="text-sm font-medium bg-gradient-to-r from-purple-700 to-indigo-600 bg-clip-text text-transparent">{badge}</p>
        </motion.div>
      )}
      
      <motion.h2 
        variants={itemVariants}
        className="text-3xl md:text-4xl font-bold mb-5 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent"
      >
        {title}
      </motion.h2>
      
      <motion.p 
        variants={itemVariants}
        className="max-w-2xl mx-auto text-lg text-gray-600 leading-relaxed"
      >
        {description}
      </motion.p>

      {/* Underline accent */}
      <motion.div 
        variants={itemVariants}
        className="w-16 h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full mx-auto mt-6 opacity-80"
      ></motion.div>
    </motion.div>
  );
};

export default SectionHeader;
